import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

export interface RangeProps {
  value: number; // 0..100
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  showMute?: boolean;
  className?: string;
}

const Range = ({
  value,
  min = 0,
  max = 100,
  step = 1,
  className = '',
  onChange,
}: RangeProps) => {
  const clamped = Math.min(max, Math.max(min, Number(value ?? 0)));

  const [localValue, setLocalValue] = useState(clamped);

  useEffect(() => {
    setLocalValue(clamped);
  }, [clamped]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(Number(e.target.value));
  };

  const handleCommit = () => {
    onChange(localValue);
  };

  return (
    <RangeWrapper className={className}>
      <Slider
        min={min}
        max={max}
        step={step}
        value={localValue}
        onChange={handleChange}
        onMouseUp={handleCommit}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={localValue}
        aria-label="value"
      />

      <ValueLabel>{localValue}%</ValueLabel>
    </RangeWrapper>
  );
};

const RangeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Slider = styled.input.attrs({ type: 'range' })`
  max-width: 100px;
  flex: 1;
  cursor: pointer;
  height: 4px;
  border-radius: 4px;
  background: linear-gradient(to right, #007aff, #7bbbff);
  appearance: none;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #007aff;
    cursor: grab;
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #007aff;
    cursor: grab;
  }
`;

const ValueLabel = styled.span`
  min-width: 32px;
  text-align: right;
  font-size: 0.9rem;
  font-family: sans-serif;
`;

export default Range;
